<script>
  import { navigate } from "svelte-routing";
  import { pies } from "../../stores/pies.js";
  import api from "../../services/api.js";

  let name = "";
  let description = "";
  let allocations = [];
  let searchQuery = "";
  let searchResults = [];
  let searching = false;
  let saving = false;
  let error = null;

  async function searchStocks() {
    if (searchQuery.length < 1) {
      searchResults = [];
      return;
    }
    searching = true;
    try {
      const results = await api.searchStocks(searchQuery);
      console.log("PieCreate search results:", results);
      searchResults = results || [];
    } catch (e) {
      console.error("Search error:", e);
      searchResults = [];
    }
    searching = false;
  }

  function addStock(stock) {
    if (allocations.find(a => a.symbol === stock.symbol)) {
      return;
    }
    allocations = [...allocations, { symbol: stock.symbol, name: stock.name, percentage: 0 }];
    searchQuery = "";
    searchResults = [];
  }

  function removeStock(symbol) {
    allocations = allocations.filter(a => a.symbol !== symbol);
  }

  function updatePercentage(symbol, value) {
    allocations = allocations.map(a =>
      a.symbol === symbol ? { ...a, percentage: parseFloat(value) || 0 } : a
    );
  }

  function distributeEvenly() {
    if (allocations.length === 0) return;
    const evenPct = Math.floor(100 / allocations.length);
    const remainder = 100 - (evenPct * allocations.length);
    allocations = allocations.map((a, i) => ({
      ...a,
      percentage: i === 0 ? evenPct + remainder : evenPct,
    }));
  }

  $: totalPercentage = allocations.reduce((sum, a) => sum + a.percentage, 0);
  $: isValid = name.trim() && allocations.length > 0 && totalPercentage === 100;

  async function handleSubmit() {
    if (!isValid) {
      error = "Please fill in all fields and ensure allocations sum to 100%";
      return;
    }
    saving = true;
    error = null;
    try {
      const pieData = {
        name: name.trim(),
        description: description.trim(),
        allocations: allocations.map(a => ({
          symbol: a.symbol,
          percentage: a.percentage,
        })),
      };
      await pies.createPie(pieData);
      navigate("/admin/pies");
    } catch (e) {
      error = e.message;
    }
    saving = false;
  }
</script>

<header class="mb-6">
  <h1 class="text-2xl font-bold text-ink">Create pie</h1>
  <p class="text-sm text-ink-secondary mt-1">Name your pie, add stocks, and set target allocations.</p>
</header>

{#if error}
  <div class="banner banner-danger mb-4 flex items-start justify-between gap-4">
    <span>{error}</span>
    <button on:click={() => error = null} class="shrink-0 text-red-800 hover:text-red-900">&times;</button>
  </div>
{/if}

<div class="card card-pad max-w-3xl">
  <div class="mb-4">
    <label class="label">
      Pie name *
    </label>
    <input type="text" bind:value={name} placeholder="My Portfolio" class="input" />
  </div>

  <div class="mb-4">
    <label class="label">
      Description
    </label>
    <textarea bind:value={description} placeholder="Optional description" rows="2" class="input"></textarea>
  </div>

  <div class="mb-4">
    <label class="label">
      Add stocks
    </label>
    <div class="relative">
      <input type="text" bind:value={searchQuery} on:input={searchStocks} placeholder="Search stocks (e.g., AAPL, MSFT)" class="input" />
      {#if searching}
        <div class="absolute right-4 top-1/2 -translate-y-1/2">
          <i class="fas fa-spinner fa-spin text-ink-tertiary"></i>
        </div>
      {/if}
      {#if searchResults.length > 0}
        <div class="absolute z-10 w-full bg-surface border border-line rounded-xl shadow-sm mt-1 max-h-48 overflow-y-auto">
          {#each searchResults as stock}
            <button on:click={() => addStock(stock)} class="w-full text-left px-4 py-2.5 hover:bg-surface-variant">
              <span class="text-sm font-medium text-ink">{stock.symbol}</span>
              <span class="text-sm text-ink-tertiary ml-2">{stock.name}</span>
            </button>
          {/each}
        </div>
      {/if}
    </div>
  </div>

  {#if allocations.length > 0}
    <div class="mb-4">
      <div class="flex justify-between items-center mb-2">
        <span class="label !mb-0">
          Allocations <span class="font-normal text-ink-secondary">(total: <span class="tnum">{totalPercentage}%</span>)</span>
        </span>
        <button on:click={distributeEvenly} class="text-sm font-medium text-moira hover:text-moira-dark transition-colors">
          Distribute evenly
        </button>
      </div>
      <div class="space-y-2">
        {#each allocations as allocation}
          <div class="flex items-center gap-3 rounded-xl bg-surface-variant p-3">
            <span class="text-sm font-semibold text-ink w-20 shrink-0">{allocation.symbol}</span>
            <span class="text-sm text-ink-secondary flex-1 truncate">{allocation.name}</span>
            <input type="number" min="0" max="100" value={allocation.percentage} on:input={(e) => updatePercentage(allocation.symbol, e.target.value)} class="input !w-24 !py-2 text-right tnum !bg-surface" />
            <span class="text-sm text-ink-secondary">%</span>
            <button on:click={() => removeStock(allocation.symbol)} class="px-1 text-ink-tertiary hover:text-red-600 transition-colors">
              <i class="fas fa-times"></i>
            </button>
          </div>
        {/each}
      </div>
      {#if totalPercentage !== 100}
        <p class="text-sm text-amber-600 mt-2">
          <i class="fas fa-exclamation-triangle mr-1"></i>
          Allocations must sum to 100% (currently <span class="tnum">{totalPercentage}%</span>)
        </p>
      {/if}
    </div>
  {/if}

  <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-line">
    <a href="/admin/pies" class="btn btn-outline">
      Cancel
    </a>
    <button on:click={handleSubmit} disabled={!isValid || saving} class="btn btn-primary">
      {#if saving}
        <i class="fas fa-spinner fa-spin"></i>
      {/if}
      Create pie
    </button>
  </div>
</div>
